import { useState, useEffect, useCallback } from 'react'
import { DollarSign, Plus, Percent, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/utils/cn'
import { financeV2Service } from '../services/financeV2Service'
import { useDateTimeFormat } from '../hooks/useDateTimeFormat'
import { CreateContingencyFeeModal } from './CreateContingencyFeeModal'
import { CollectContingencyFeeModal } from './CollectContingencyFeeModal'
import type { ContingencyFee } from '../types/financeV2.types'

interface CaseContingencyFeesProps {
  caseId: string
  clientId: string
  caseTitle: string
}

export function CaseContingencyFees({ caseId, clientId, caseTitle }: CaseContingencyFeesProps) {
  const [fees, setFees] = useState<ContingencyFee[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showCollectModal, setShowCollectModal] = useState(false)
  const [selectedFee, setSelectedFee] = useState<ContingencyFee | null>(null)
  const { formatDateTime, formatCurrency } = useDateTimeFormat()

  const loadFees = useCallback(async () => {
    try {
      setLoading(true)
      const response = await financeV2Service.contingencyFees.getByCase(caseId)
      if (response.success) {
        setFees(response.data.fees)
      }
    } catch (error) {
      console.error('Error loading contingency fees:', error)
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    loadFees()
  }, [loadFees])

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="secondary"><Clock aria-hidden="true" /> Ativo</Badge>
      case 'COLLECTED':
        return <Badge><CheckCircle aria-hidden="true" /> Cobrado</Badge>
      case 'CANCELLED':
        return <Badge variant="outline"><XCircle aria-hidden="true" /> Cancelado</Badge>
      default:
        return null
    }
  }

  const activeFees = fees.filter(f => f.status === 'ACTIVE')
  const collectedFees = fees.filter(f => f.status === 'COLLECTED')
  const totalCollected = collectedFees.reduce((sum, f) => sum + (f.collected_value || 0), 0)

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          <Percent className="size-4 text-muted-foreground" aria-hidden="true" />
          <h2>Honorários Contingentes</h2>
        </CardTitle>
        <Button size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="size-4" /> Adicionar
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {loading ? (
          <div className="flex flex-col gap-2" aria-busy="true">
            <span className="sr-only">Carregando honorários...</span>
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        ) : fees.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground">
            <Percent className="mx-auto mb-3 size-10 opacity-30" aria-hidden="true" />
            <p className="text-sm">Nenhum honorário contingente configurado para este caso</p>
            <p className="mt-1 text-xs">Clique em &quot;Adicionar&quot; para criar um honorário de êxito</p>
          </div>
        ) : (
          <>
            {activeFees.length > 0 && (
              <div role="note" className="flex items-start gap-3 rounded-md border bg-muted/40 p-3">
                <AlertCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div>
                  <h4 className="text-sm font-medium">
                    {activeFees.length} honorário{activeFees.length > 1 ? 's' : ''} ativo{activeFees.length > 1 ? 's' : ''}
                  </h4>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Quando o caso for encerrado, você receberá uma notificação para cobrar {activeFees.length > 1 ? 'estes honorários' : 'este honorário'}.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between gap-2 rounded-lg border p-3">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Honorários Ativos</p>
                  <p className="text-xl font-semibold tabular-nums">{activeFees.length}</p>
                </div>
                <Clock className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border p-3">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Total Cobrado</p>
                  <p className="truncate text-xl font-semibold tabular-nums">{formatCurrency(totalCollected)}</p>
                </div>
                <DollarSign className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
              </div>
            </div>

            <ul className="flex flex-col gap-3">
              {fees.map((fee) => (
                <li key={fee.id} className={cn('rounded-lg border p-3', fee.status === 'CANCELLED' && 'bg-muted/40')}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="text-lg font-semibold tabular-nums">{fee.percentage}%</span>
                        {getStatusBadge(fee.status)}
                      </div>
                      <p className="mb-2 text-sm">{fee.description}</p>
                      <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                        {fee.cost_center_name && <div>Centro de custo: {fee.cost_center_name}</div>}
                        <div>Criado por: {fee.created_by_name || 'N/A'}</div>
                        <div>Criado em: {formatDateTime(fee.created_at)}</div>
                      </div>
                      {fee.status === 'COLLECTED' && fee.collected_value && (
                        <div className="mt-3 border-t pt-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium">Valor Cobrado:</span>
                            <span className="text-lg font-semibold tabular-nums">{formatCurrency(fee.collected_value)}</span>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            Cobrado em: {fee.collected_at ? formatDateTime(fee.collected_at) : 'N/A'}
                          </div>
                        </div>
                      )}
                    </div>
                    {fee.status === 'ACTIVE' && (
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedFee(fee)
                          setShowCollectModal(true)
                        }}
                      >
                        <DollarSign className="size-3.5" /> Cobrar
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>

      {/* Create Contingency Fee Modal */}
      <CreateContingencyFeeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        caseId={caseId}
        clientId={clientId}
        caseTitle={caseTitle}
        onSuccess={() => {
          loadFees()
          setShowCreateModal(false)
        }}
      />

      {/* Collect Contingency Fee Modal */}
      {selectedFee && (
        <CollectContingencyFeeModal
          isOpen={showCollectModal}
          onClose={() => {
            setShowCollectModal(false)
            setSelectedFee(null)
          }}
          fee={{
            id: selectedFee.id,
            percentage: selectedFee.percentage,
            description: selectedFee.description,
            case_title: caseTitle
          }}
          onSuccess={() => {
            loadFees()
            setShowCollectModal(false)
            setSelectedFee(null)
          }}
        />
      )}
    </Card>
  )
}
